import { useState, useRef, useEffect, useCallback } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { html } from "@codemirror/lang-html";
import { Toolbar } from "./Toolbar";
import { LinkDialog } from "./LinkDialog";
import { RichTextEditorProps, EditorMode } from "./types";
import { cn } from "@/lib/utils";

export const RichTextEditor = ({
  initialValue = "",
  onChange,
  onFocus,
  onBlur,
  onImageUpload,
  placeholder = "Start typing...",
  toolbarConfig,
  disableImages,
  disableVideos,
  disableTables,
  showToolbar = true,
  className,
  height = "auto",
  minHeight = "300px",
  maxHeight,
  theme = "light",
  airMode = false,
  enableCodeView = true,
  enableFullscreen = true,
}: RichTextEditorProps) => {
  const [content, setContent] = useState(initialValue);
  const [mode, setMode] = useState<EditorMode>("wysiwyg");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());

  // Link dialog state
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkDialogData, setLinkDialogData] = useState<{
    selectedText: string;
    hasSelection: boolean;
    isEditing: boolean;
    currentUrl?: string;
    linkElement?: HTMLAnchorElement;
    storedRange?: Range | null;
  }>({ selectedText: "", hasSelection: false, isEditing: false });

  // Persistent style states
  const [currentFontName, setCurrentFontName] = useState<string>("");
  const [currentFontSize, setCurrentFontSize] = useState<string>("");
  const [currentFormatBlock, setCurrentFormatBlock] = useState<string>("p");
  const [currentForeColor, setCurrentForeColor] = useState<string>("#000000");
  const [currentBackColor, setCurrentBackColor] = useState<string>("#ffffff");

  const editorRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const isApplyingStyle = useRef(false);

  useEffect(() => {
    if (
      editorRef.current &&
      mode === "wysiwyg" &&
      !editorRef.current.innerHTML
    ) {
      editorRef.current.innerHTML = content;
    }
    // Only run on initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateActiveFormats = useCallback(() => {
    if (mode !== "wysiwyg") return;

    const formats = new Set<string>();
    if (document.queryCommandState("bold")) formats.add("bold");
    if (document.queryCommandState("italic")) formats.add("italic");
    if (document.queryCommandState("underline")) formats.add("underline");
    if (document.queryCommandState("strikethrough"))
      formats.add("strikethrough");
    if (document.queryCommandState("insertUnorderedList"))
      formats.add("insertUnorderedList");
    if (document.queryCommandState("insertOrderedList"))
      formats.add("insertOrderedList");
    if (document.queryCommandState("justifyLeft")) formats.add("justifyLeft");
    if (document.queryCommandState("justifyCenter"))
      formats.add("justifyCenter");
    if (document.queryCommandState("justifyRight")) formats.add("justifyRight");
    if (document.queryCommandState("justifyFull")) formats.add("justifyFull");

    setActiveFormats(formats);
  }, [mode]);

  const handleInput = () => {
    if (editorRef.current && !isApplyingStyle.current) {
      const newContent = editorRef.current.innerHTML;
      setContent(newContent);
      onChange?.(newContent);
      updateActiveFormats();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mode !== "wysiwyg") return;

    // Handle printable characters that will add text
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const selection = window.getSelection();
      if (
        selection &&
        selection.isCollapsed &&
        (currentFontSize ||
          currentFontName ||
          currentForeColor !== "#000000" ||
          currentBackColor !== "#ffffff")
      ) {
        e.preventDefault();

        // Create a styled span with the character
        const span = document.createElement("span");
        const styles: string[] = [];

        if (currentFontSize && currentFontSize !== "") {
          styles.push(`font-size: ${currentFontSize}`);
        }
        if (currentFontName && currentFontName !== "") {
          styles.push(`font-family: "${currentFontName}"`);
        }
        if (currentForeColor && currentForeColor !== "#000000") {
          styles.push(`color: ${currentForeColor}`);
        }
        if (currentBackColor && currentBackColor !== "#ffffff") {
          styles.push(`background-color: ${currentBackColor}`);
        }

        if (styles.length > 0) {
          span.style.cssText = styles.join("; ");
        }

        span.textContent = e.key;

        // Insert the styled span
        const range = selection.getRangeAt(0);
        range.insertNode(span);

        // Move cursor after the inserted character
        range.setStartAfter(span);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);

        // Trigger input event
        handleInput();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (mode !== "wysiwyg") return;

    e.preventDefault();

    // Get the pasted content as HTML to preserve formatting
    const clipboardData = e.clipboardData;
    const htmlData = clipboardData.getData("text/html");
    const textData = clipboardData.getData("text/plain");

    // If HTML data exists, use it to preserve formatting; otherwise use plain text
    const pastedContent = htmlData || textData;

    if (pastedContent) {
      document.execCommand("insertHTML", false, pastedContent);
      handleInput();
    }
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "A") {
      e.preventDefault();
      const link = target as HTMLAnchorElement;

      setLinkDialogData({
        selectedText: link.textContent || "",
        hasSelection: true,
        isEditing: true,
        currentUrl: link.href,
        linkElement: link,
        storedRange: null,
      });

      setShowLinkDialog(true);
    }
  };

  const handleLinkDoubleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "A") {
      e.preventDefault();
      const link = target as HTMLAnchorElement;
      // Open link in new tab on double-click
      window.open(link.href, "_blank");
    }
  };

  const applyPersistentStyles = () => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);

    // Check if we're at the end of the content and need to apply styles
    if (range.collapsed && range.startContainer.nodeType === Node.TEXT_NODE) {
      const textNode = range.startContainer as Text;
      const parent = textNode.parentElement;

      if (parent && parent.isContentEditable) {
        isApplyingStyle.current = true;

        // Create a temporary marker to identify the cursor position
        const marker = document.createElement("span");
        marker.id = "cursor-marker";
        marker.style.display = "inline";
        range.insertNode(marker);

        // Apply styles by wrapping new text in a span with the current styles
        const styleSpan = document.createElement("span");
        const styles: string[] = [];

        if (currentFontSize && currentFontSize !== "") {
          styles.push(`font-size: ${currentFontSize}`);
        }
        if (currentFontName && currentFontName !== "") {
          styles.push(`font-family: "${currentFontName}"`);
        }
        if (currentForeColor && currentForeColor !== "#000000") {
          styles.push(`color: ${currentForeColor}`);
        }
        if (currentBackColor && currentBackColor !== "#ffffff") {
          styles.push(`background-color: ${currentBackColor}`);
        }

        if (styles.length > 0) {
          styleSpan.style.cssText = styles.join("; ");
          styleSpan.innerHTML = "&nbsp;"; // Add a non-breaking space

          // Replace marker with styled span
          marker.replaceWith(styleSpan);

          // Move cursor to end of the styled span
          const newRange = document.createRange();
          newRange.setStartAfter(styleSpan);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        } else {
          // Remove marker if no styles to apply
          marker.remove();
        }

        isApplyingStyle.current = false;
      }
    }
  };

  const handleCodeChange = (value: string) => {
    setContent(value);
    onChange?.(value);
  };

  // Format HTML for better display in code view
  const formatHtmlForCodeView = (html: string) => {
    if (!html || html.trim() === "") return "";

    // Clean up the HTML and format it properly
    const formatted = html
      .replace(/></g, ">\n<")
      .replace(/^\s+|\s+$/g, "")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .join("\n");

    // Add proper indentation
    let indentLevel = 0;
    const indentSize = 2;
    const lines = formatted.split("\n");

    return lines
      .map((line) => {
        if (
          line.includes("</") &&
          !line.includes("</" + line.match(/<\/(\w+)>/)?.[1] + ">")
        ) {
          indentLevel = Math.max(0, indentLevel - 1);
        }

        const indentedLine = " ".repeat(indentLevel * indentSize) + line;

        if (
          line.includes("<") &&
          !line.includes("</") &&
          !line.includes("/>")
        ) {
          indentLevel++;
        }

        return indentedLine;
      })
      .join("\n");
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (onImageUpload) {
      const url = await onImageUpload(file);
      if (url) {
        document.execCommand("insertImage", false, url);
        handleInput();
      }
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        const url = event.target?.result as string;
        document.execCommand("insertImage", false, url);
        handleInput();
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const url = event.target?.result as string;
      const embedHtml = `<video src="${url}" controls width="560" height="315"></video>`;
      document.execCommand("insertHTML", false, embedHtml);
      handleInput();
    };
    reader.readAsDataURL(file);
  };

  const executeCommand = (command: string, value?: string) => {
    if (mode !== "wysiwyg") return;

    editorRef.current?.focus();

    if (command === "createLink") {
      const selection = window.getSelection();
      const selectedText = selection?.toString() || "";

      // Store the current selection range to restore it later
      let storedRange: Range | null = null;
      if (selection && selection.rangeCount > 0 && selectedText.length > 0) {
        storedRange = selection.getRangeAt(0).cloneRange();
      }

      setLinkDialogData({
        selectedText,
        hasSelection: selectedText.length > 0,
        isEditing: false,
        storedRange,
      });
      setShowLinkDialog(true);
      return;
    } else if (command === "insertImage") {
      imageInputRef.current?.click();
      return;
    } else if (command === "insertVideo") {
      videoInputRef.current?.click();
      return;
    } else if (command === "insertTable") {
      const rows = prompt("Number of rows:", "3");
      const cols = prompt("Number of columns:", "3");
      if (rows && cols) {
        let tableHtml = "<table>";
        for (let i = 0; i < parseInt(rows); i++) {
          tableHtml += "<tr>";
          for (let j = 0; j < parseInt(cols); j++) {
            tableHtml += "<td>&nbsp;</td>";
          }
          tableHtml += "</tr>";
        }
        tableHtml += "</table>";
        document.execCommand("insertHTML", false, tableHtml);
      }
    } else if (command === "fontSize") {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
        // Apply to selected text only
        const selectedText = selection.toString();
        const span = `<span style="font-size: ${
          value || "16px"
        }">${selectedText}</span>`;
        document.execCommand("insertHTML", false, span);
      }
      // Update state for future text
      setCurrentFontSize(value || "");

      // If no text is selected, prepare the cursor position for the new font size
      if (selection && selection.isCollapsed) {
        setTimeout(() => applyPersistentStyles(), 0);
      }
    } else if (command === "formatBlock") {
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        // Apply to selected text/block only
        document.execCommand("formatBlock", false, `<${value}>`);
      }
      // Update state for future text
      setCurrentFormatBlock(value || "p");
    } else if (command === "fontName") {
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        // Apply to selected text only
        document.execCommand("fontName", false, value);
      }
      // Update state for future text
      setCurrentFontName(value || "");
    } else if (
      command === "insertUnorderedList" ||
      command === "insertOrderedList"
    ) {
      document.execCommand(command, false);
    } else if (command === "indent" || command === "outdent") {
      document.execCommand(command, false);
    } else if (command === "foreColor") {
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        // Apply to selected text only
        document.execCommand("foreColor", false, value);
      }
      // Update state for future text
      setCurrentForeColor(value || "#000000");
    } else if (command === "backColor") {
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        // Apply to selected text only
        document.execCommand("backColor", false, value);
      }
      // Update state for future text
      setCurrentBackColor(value || "#ffffff");
    } else {
      document.execCommand(command, false, value);
    }

    handleInput();
  };

  const toggleMode = () => {
    if (mode === "wysiwyg") {
      // When switching to code mode, get the HTML source and format it
      if (editorRef.current) {
        const htmlContent = editorRef.current.innerHTML;
        console.log("Switching to code mode, HTML content:", htmlContent);
        const formattedHtml = formatHtmlForCodeView(htmlContent);
        console.log("Formatted HTML for code view:", formattedHtml);
        setContent(formattedHtml);
      }
      setMode("code");
    } else {
      // When switching back to WYSIWYG mode, set the HTML content
      console.log("Switching to WYSIWYG mode, content:", content);
      setMode("wysiwyg");
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = content;
        }
      }, 0);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleLinkSave = (url: string, text?: string) => {
    editorRef.current?.focus();

    if (linkDialogData.isEditing && linkDialogData.linkElement) {
      // Update existing link
      linkDialogData.linkElement.href = url;
      linkDialogData.linkElement.title = url;
      linkDialogData.linkElement.setAttribute("data-link", url);
    } else if (linkDialogData.hasSelection && linkDialogData.storedRange) {
      // Replace selected text with link using the stored range
      const selection = window.getSelection();
      if (selection) {
        // Restore the original selection
        selection.removeAllRanges();
        selection.addRange(linkDialogData.storedRange);

        // Create the link element
        const linkElement = document.createElement("a");
        linkElement.href = url;
        linkElement.target = "_blank";
        linkElement.title = url;
        linkElement.style.color = "#3b82f6";
        linkElement.style.textDecoration = "underline";
        linkElement.setAttribute("data-link", url);
        linkElement.textContent = linkDialogData.selectedText;

        // Replace the selected content with the link
        const range = linkDialogData.storedRange;
        range.deleteContents();
        range.insertNode(linkElement);

        // Position cursor after the link
        range.setStartAfter(linkElement);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } else {
      // Insert new link with provided text at cursor position
      const linkText = text || url;
      const linkHtml = `<a href="${url}" target="_blank" title="${url}" style="color: #3b82f6; text-decoration: underline;" data-link="${url}">${linkText}</a>`;
      document.execCommand("insertHTML", false, linkHtml);
    }

    handleInput();
  };

  const handleLinkDialogClose = () => {
    setShowLinkDialog(false);
    setLinkDialogData({
      selectedText: "",
      hasSelection: false,
      isEditing: false,
      storedRange: null,
    });
  };

  return (
    <div
      className={cn(
        "flex flex-col border border-editor-border rounded-lg overflow-hidden bg-editor-bg shadow-none transition-smooth",
        isFullscreen && "fixed inset-4 z-50",
        !airMode && "shadow-elegant",
        airMode && "border-0 shadow-none",
        className
      )}
      style={{
        height: isFullscreen ? "calc(100vh - 2rem)" : height,
      }}
    >
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={handleVideoUpload}
        className="hidden"
      />

      {showToolbar && (
        <Toolbar
          onCommand={executeCommand}
          onModeToggle={toggleMode}
          onFullscreenToggle={toggleFullscreen}
          mode={mode}
          isFullscreen={isFullscreen}
          disableImages={disableImages}
          disableVideos={disableVideos}
          disableTables={disableTables}
          activeFormats={activeFormats}
          currentFontName={currentFontName}
          currentFontSize={currentFontSize}
          currentFormatBlock={currentFormatBlock}
          currentForeColor={currentForeColor}
          currentBackColor={currentBackColor}
          toolbarConfig={toolbarConfig}
        />
      )}

      {/* WYSIWYG Editor */}
      <div
        className={cn("flex-1 overflow-hidden", mode !== "wysiwyg" && "hidden")}
      >
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onClick={handleLinkClick}
          onDoubleClick={handleLinkDoubleClick}
          onFocus={onFocus}
          onBlur={onBlur}
          onMouseUp={updateActiveFormats}
          onKeyUp={updateActiveFormats}
          className={cn(
            `flex-1 p-4 ${
              content ? "py-1" : "py-2"
            } outline-none overflow-auto prose prose-sm max-w-none transition-smoothv`
          )}
          style={{
            minHeight,
            maxHeight: isFullscreen ? undefined : maxHeight,
          }}
          data-placeholder={placeholder}
        />
      </div>

      {/* Code Editor */}
      <div
        className={cn("flex-1 overflow-hidden", mode !== "code" && "hidden")}
      >
        <CodeMirror
          value={content}
          height={isFullscreen ? "calc(100vh - 8rem)" : undefined}
          minHeight={minHeight}
          maxHeight={isFullscreen ? undefined : maxHeight}
          extensions={[html()]}
          onChange={handleCodeChange}
          theme={theme === "dark" ? "dark" : "light"}
          className="w-full h-full"
          editable={true}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            dropCursor: false,
            allowMultipleSelections: false,
            indentOnInput: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: true,
            highlightSelectionMatches: false,
          }}
        />
      </div>

      {/* Link Dialog */}
      <LinkDialog
        isOpen={showLinkDialog}
        onClose={handleLinkDialogClose}
        onSave={handleLinkSave}
        initialUrl={linkDialogData.currentUrl}
        initialText={linkDialogData.hasSelection ? undefined : ""}
        hasSelection={linkDialogData.hasSelection}
      />

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: hsl(var(--muted-foreground));
          pointer-events: none;
          position: absolute;
        }
        
        /* Preserve original content styling - minimal interference */
        [contenteditable] * {
          /* Preserve all existing styles from pasted content */
        }
        
        /* Only apply default styles when no inline styles are present */
        [contenteditable] h1:not([style*="font-size"]) {
          font-size: 2.5em;
        }
        [contenteditable] h1 {
          font-weight: 700;
          line-height: 1.2;
          margin: 0.67em 0;
        }
        [contenteditable] h2:not([style*="font-size"]) {
          font-size: 2em;
        }
        [contenteditable] h2 {
          font-weight: 700;
          line-height: 1.3;
          margin: 0.75em 0;
        }
        [contenteditable] h3:not([style*="font-size"]) {
          font-size: 1.75em;
        }
        [contenteditable] h3 {
          font-weight: 600;
          line-height: 1.4;
          margin: 0.83em 0;
        }
        [contenteditable] h4:not([style*="font-size"]) {
          font-size: 1.5em;
        }
        [contenteditable] h4 {
          font-weight: 600;
          line-height: 1.4;
          margin: 1em 0;
        }
        [contenteditable] h5:not([style*="font-size"]) {
          font-size: 1.25em;
        }
        [contenteditable] h5 {
          font-weight: 600;
          line-height: 1.5;
          margin: 1.33em 0;
        }
        [contenteditable] h6:not([style*="font-size"]) {
          font-size: 1em;
        }
        [contenteditable] h6 {
          font-weight: 600;
          line-height: 1.5;
          margin: 1.67em 0;
        }
        
        /* Paragraph Styles - only when no inline styles */
        [contenteditable] p:not([style*="font-size"]) {
          font-size: 1em;
        }
        [contenteditable] p {
          line-height: 1.6;
          margin: 0.5em 0;
        }
        
        /* Link Styles - preserve existing colors */
        [contenteditable] a:not([style*="color"]) {
          color: #3b82f6;
        }
        [contenteditable] a:not([style*="text-decoration"]) {
          text-decoration: underline;
        }
        
        /* Enhanced link hover effects */
        [contenteditable] a {
          position: relative;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        [contenteditable] a:hover {
          opacity: 0.8;
          // background-color: rgba(59, 130, 246, 0.1);
          border-radius: 2px;
          // padding: 1px 2px;
        }
        
        /* Link hover tooltip */
        // [contenteditable] a:hover:before {
        //   content: attr(title);
        //   position: absolute;
        //   bottom: 100%;
        //   left: 50%;
        //   transform: translateX(-50%);
        //   background: rgba(0, 0, 0, 0.9);
        //   color: white;
        //   padding: 4px 8px;
        //   border-radius: 4px;
        //   font-size: 12px;
        //   white-space: nowrap;
        //   z-index: 1000;
        //   pointer-events: none;
        // }
        
        // [contenteditable] a:hover:after {
        //   content: "";
        //   position: absolute;
        //   bottom: calc(100% - 4px);
        //   left: 50%;
        //   transform: translateX(-50%);
        //   border: 4px solid transparent;
        //   border-top-color: rgba(0, 0, 0, 0.9);
        //   z-index: 1000;
        //   pointer-events: none;
        // }
        
        /* List Styles */
        [contenteditable] ul,
        [contenteditable] ol {
          margin: 0.5em 0;
          padding-left: 2em;
        }
        
        [contenteditable] li {
          margin: 0.25em 0;
          line-height: 1.6;
        }
        
        /* Table Styles - Minimal styling, preserve original borders and styling */
        [contenteditable] table {
          margin: 1em 0;
        }
        
        [contenteditable] td,
        [contenteditable] th {
          min-width: 20px;
          min-height: 20px;
        }
        
        /* Only add padding if not already specified */
        [contenteditable] td:not([style*="padding"]),
        [contenteditable] th:not([style*="padding"]) {
          padding: 4px;
        }
        
        [contenteditable] th:not([style*="font-weight"]) {
          font-weight: 600;
        }
        
        /* Divs should not have any default styling */
        [contenteditable] div {
          /* Preserve all original styles */
        }
        
        /* Ensure inline styles take precedence */
        [contenteditable] [style*="font-size"] {
          line-height: 1.4;
        }
        
        /* Prevent content overflow in code view and WYSIWYG */
        [contenteditable] {
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        
        /* Prevent tables from causing overflow */
        [contenteditable] table {
          table-layout: auto;
          max-width: 100%;
        }
        
        /* Ensure code blocks don't overflow */
        [contenteditable] pre,
        [contenteditable] code {
          white-space: pre-wrap;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
      `}</style>
    </div>
  );
};
